import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AccreditationPage from './pages/AccreditationPage';
import DocumentsPage from './pages/DocumentsPage';
import ZedPage from './pages/ZedPage';
import './new-pages.css';

const ROUTES = {
  '/':              { page: 'Home',          Component: HomePage },
  '/services':      { page: 'Services',      Component: ServicesPage },
  '/about':         { page: 'About',         Component: AboutPage },
  '/contact':       { page: 'Contact',       Component: ContactPage },
  '/accreditation': { page: 'Accreditation', Component: AccreditationPage },
  '/documents':     { page: 'Documents',     Component: DocumentsPage },
  '/zed':           { page: 'Zed',           Component: ZedPage },
};

/** Per-page SEO metadata */
const PAGE_META = {
  Home: {
    title: 'AQMCS | ISO Certification, ZED Assessment & Food Safety Audit India',
    description: 'AQMCS — NABCB-accredited inspection body offering ISO certification, ZED Assessment for MSMEs, Food Safety Audits & Third-Party Audits across India. Trusted since 2006. Call +91-92161-83238.',
    canonical: 'https://www.aqmcs.com/',
  },
  Services: {
    title: 'Services | ISO, ZED Assessment, Food Safety Audit | AQMCS India',
    description: 'Explore AQMCS services: ZED Assessment for MSMEs, Food Safety Audit (FSSAI), ISO Certification, Third-Party Audit, Gender Equality Audit & Green Audit across India.',
    canonical: 'https://www.aqmcs.com/services',
  },
  About: {
    title: 'About AQMCS | NABCB-Accredited Inspection Body Since 2006',
    description: 'AQMCS is an independent, NABCB-accredited Type-A Inspection Body operating since 2006. Learn about our expertise in ISO certification, ZED Assessment and food safety audits across India.',
    canonical: 'https://www.aqmcs.com/about',
  },
  Contact: {
    title: 'Contact AQMCS | Certification & Audit Enquiries | Panchkula, India',
    description: 'Get in touch with AQMCS for ISO certification, ZED Assessment and food safety audit enquiries. Office: Sector 11, Panchkula, Haryana. Call +91-92161-83238 or email aqmcs@aqmcs.com.',
    canonical: 'https://www.aqmcs.com/contact',
  },
  Accreditation: {
    title: 'Accreditation & Recognition | NABCB, QCI, FSSAI, IOCL | AQMCS',
    description: 'AQMCS holds NABCB accreditation (ISO/IEC 17020), QCI empanelment as ZED Assessment Agency, FSSAI recognition as HRAA & TPAA, and is empanelled with IOCL and NTPC.',
    canonical: 'https://www.aqmcs.com/accreditation',
  },
  Documents: {
    title: 'Documents & Downloads | Application Forms, Policies | AQMCS',
    description: 'Download AQMCS official documents: application forms for FBO, BEMC, ZED; complaint & appeal forms; impartiality policy; code of conduct and more.',
    canonical: 'https://www.aqmcs.com/documents',
  },
  Zed: {
    title: 'ZED Certification for MSMEs | Zero Defect Zero Effect | AQMCS',
    description: 'Get ZED Certification (Zero Defect Zero Effect) for your MSME through AQMCS — a QCI-empanelled ZED Assessment Agency. Benefits include government subsidies, improved quality & global competitiveness.',
    canonical: 'https://www.aqmcs.com/zed',
  },
};

/** Per-page structured data (JSON-LD) */
const PAGE_SCHEMA = {
  Services: JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "AQMCS Services",
    "url": "https://www.aqmcs.com/services",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "ZED Assessment", "url": "https://www.aqmcs.com/zed" },
      { "@type": "ListItem", "position": 2, "name": "Food Safety Audit", "url": "https://www.aqmcs.com/services" },
      { "@type": "ListItem", "position": 3, "name": "ISO Certification", "url": "https://www.aqmcs.com/services" },
      { "@type": "ListItem", "position": 4, "name": "Third Party Audit", "url": "https://www.aqmcs.com/services" },
      { "@type": "ListItem", "position": 5, "name": "Gender Equality Audit", "url": "https://www.aqmcs.com/services" },
      { "@type": "ListItem", "position": 6, "name": "Green Audit", "url": "https://www.aqmcs.com/services" },
    ]
  }),
  Contact: JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact AQMCS",
    "url": "https://www.aqmcs.com/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "AQMCS",
      "telephone": "+91-92161-83238",
      "email": "aqmcs@aqmcs.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "SCF 97 2nd Floor, Sector 11",
        "addressLocality": "Panchkula",
        "addressRegion": "Haryana",
        "postalCode": "134113",
        "addressCountry": "IN"
      }
    }
  }),
  Zed: JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "ZED Assessment & Certification",
    "provider": { "@type": "Organization", "name": "AQMCS", "url": "https://www.aqmcs.com" },
    "description": "ZED (Zero Defect Zero Effect) Assessment and Certification for Indian MSMEs. QCI-empanelled agency offering end-to-end ZED assessment services.",
    "url": "https://www.aqmcs.com/zed",
    "areaServed": { "@type": "Country", "name": "India" },
    "audience": { "@type": "Audience", "audienceType": "MSME, Small and Medium Enterprises" }
  }),
  About: JSON.stringify({
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About AQMCS",
    "url": "https://www.aqmcs.com/about",
    "description": "AQMCS is an independent and impartial NABCB-accredited Type-A Inspection Body operating since 2006.",
    "mainEntity": { "@id": "https://www.aqmcs.com/#organization" }
  }),
};

/** Inject / update a <meta> tag by name or property */
function setMeta(attr, val, content) {
  let el = document.querySelector(`meta[${attr}="${val}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, val);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Inject / update a <link rel="canonical"> */
function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/** Update per-page JSON-LD (replaces the dynamic slot, leaves static ones alone) */
function setPageSchema(schema) {
  const ID = 'ld-page-dynamic';
  let el = document.getElementById(ID);
  if (!schema) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = ID;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = schema;
}

function resolveRoute() {
  const path = window.location.pathname;
  return ROUTES[path] || ROUTES['/'];
}

function MarqueeNews({ items = [], speed = 40 }) {
  const doubled = [...items, ...items];
  const duration = `${Math.max(items.length * speed, 20)}s`;
  return (
    <div className="mq-wrap" aria-label="Latest updates" role="marquee">
      <span className="mq-label">News</span>
      <div className="mq-track-wrap">
        <div className="mq-track" style={{ animationDuration: duration }}>
          {doubled.map((item, i) => (
            <span key={i} className="mq-item">
              <span className="mq-dot" aria-hidden="true" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

const PAGE_TO_PATH = {
  Home:          '/',
  Services:      '/services',
  About:         '/about',
  Contact:       '/contact',
  Accreditation: '/accreditation',
  Documents:     '/documents',
  Zed:           '/zed',
};

export default function App() {
  const [route, setRoute] = useState(resolveRoute);
  const [bttVisible, setBttVisible] = useState(false);

  const navigate = (pageKey) => {
    const path = PAGE_TO_PATH[pageKey] || '/';
    window.history.pushState({ pageKey }, '', path);
    setRoute(ROUTES[path] || ROUTES['/']);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const onPopState = () => setRoute(resolveRoute());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /** Update all meta tags whenever the page changes */
  useEffect(() => {
    const meta = PAGE_META[route.page] || PAGE_META.Home;
    document.title = meta.title;
    setMeta('name', 'description', meta.description);
    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', meta.canonical);
    setMeta('name', 'twitter:title', meta.title);
    setMeta('name', 'twitter:description', meta.description);
    setCanonical(meta.canonical);
    setPageSchema(PAGE_SCHEMA[route.page] || null);
  }, [route]);

  useEffect(() => {
    const handler = () => setBttVisible(window.scrollY > 400);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const { page, Component: PageComponent } = route;

  return (
    <>
      <div className="announce-bar1" aria-hidden="true"> </div>

      <MarqueeNews items={[
        'AQMCS is a NABCB Accredited Type-A Inspection Body in accordance with ISO/IEC 17020',
        'AQMCS has empanelled with QCI as ZED Assessment Agency for MSME Sustainable (ZED) Certification Scheme',
        'AQMCS is recognised by FSSAI as HRAA and TPAA for food safety audit.',
        'AQMCS empanelled with IOCL and NTPC as TPIA for tender documents verification',
      ]} />

      <Navbar page={page} navigate={navigate} />

      <PageComponent setPage={navigate} />

      <Footer navigate={navigate} />

      {/* WhatsApp Float Button */}
      <button
        className="wa-float"
        aria-label="Chat with AQMCS on WhatsApp"
        title="Chat on WhatsApp"
        onClick={() => window.open('https://wa.me/919216183238', '_blank')}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      </button>

      {/* Back to Top */}
      <button
        className={`btt${bttVisible ? ' visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        title="Back to top"
      >
        ↑
      </button>
    </>
  );
}
